import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import AuthContext from '../../../context/auth.context';
import { useTheme } from '../../../context/theme.context';
import { canManageEmployees, readOperatorTypeId } from '../../../types/OperatorTypes';
import { BranchSelectModal } from './BranchPicker';
import {
  CloseIcon,
  EmployeesIcon,
  LocationIcon,
  LogoutIcon,
  MoonIcon,
  BusinessIcon,
  StagesIcon,
  SunIcon,
} from './product_item_components/icons';

function branchIdOf(branch) {
  return branch?._id ?? branch?.id ?? null;
}

function DashboardMenu({ isOpen, onClose, branches = [] }) {
  const navigate = useNavigate();
  const auth = React.useContext(AuthContext);
  const { isDark, toggleTheme } = useTheme();
  const showEmployees = canManageEmployees(readOperatorTypeId(auth?.token));
  const [branchPickerOpen, setBranchPickerOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) setBranchPickerOpen(false);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (branchPickerOpen) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, branchPickerOpen, onClose]);

  const openBranchSettings = (branch) => {
    const id = branchIdOf(branch);
    if (!id) return;
    setBranchPickerOpen(false);
    onClose?.();
    navigate(`/business/branch/${id}/edit`, { state: { branch } });
  };

  const handleBranchClick = () => {
    if (branches.length === 0) return;
    if (branches.length === 1) {
      openBranchSettings(branches[0]);
      return;
    }
    setBranchPickerOpen(true);
  };

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  const handleLogout = () => {
    auth?.logout?.();
    onClose?.();

    let openedFromTelegram = false;
    try {
      openedFromTelegram = Boolean(WebApp.initData || window.Telegram?.WebApp?.initData);
    } catch (_) {
      openedFromTelegram = false;
    }

    if (openedFromTelegram) {
      try {
        WebApp.close();
        return;
      } catch (_) {
        // Older Telegram clients cannot close the mini app.
      }
    }

    navigate('/login');
  };

  if (!isOpen) return null;

  const menu = (
    <div className="dashboard-menuOverlay" onClick={() => onClose?.()}>
      <div
        className="dashboard-menuPanel"
        role="dialog"
        aria-modal="true"
        aria-label="Меню"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dashboard-menuHeader">
          <div className="dashboard-menuTitle">Меню</div>
          <button
            type="button"
            className="main-page-iconButton"
            onClick={() => onClose?.()}
            aria-label="Закрыть"
            title="Закрыть"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="dashboard-menuList">
          <button type="button" className="dashboard-menuItem" onClick={() => go('/business/update')}>
            <BusinessIcon className="dashboard-menuItemIcon dashboard-menuItemIcon--shop" />
            <span>Настройки бизнеса</span>
          </button>
          <button
            type="button"
            className="dashboard-menuItem"
            onClick={handleBranchClick}
            disabled={branches.length === 0}
          >
            <LocationIcon className="dashboard-menuItemIcon dashboard-menuItemIcon--branch" />
            <span>Настройки филиала</span>
          </button>
          <button type="button" className="dashboard-menuItem" onClick={() => go('/order-statuses')}>
            <StagesIcon className="dashboard-menuItemIcon dashboard-menuItemIcon--stages" />
            <span>Этапы (статусы заказов)</span>
          </button>
          {showEmployees ? (
            <button type="button" className="dashboard-menuItem" onClick={() => go('/employees')}>
              <EmployeesIcon className="dashboard-menuItemIcon dashboard-menuItemIcon--employees" />
              <span>Сотрудники</span>
            </button>
          ) : null}
          <button
            type="button"
            className="dashboard-menuItem"
            onClick={toggleTheme}
            aria-pressed={isDark}
            aria-label={isDark ? 'Включить светлую тему' : 'Включить тёмную тему'}
          >
            {isDark ? (
              <SunIcon className="dashboard-menuItemIcon dashboard-menuItemIcon--theme" />
            ) : (
              <MoonIcon className="dashboard-menuItemIcon dashboard-menuItemIcon--theme" />
            )}
            <span>Тема ({isDark ? 'светлая' : 'тёмная'})</span>
          </button>
        </div>

        <button type="button" className="dashboard-menuLogout" onClick={handleLogout}>
          <LogoutIcon className="dashboard-menuItemIcon" />
          <span>Выйти</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
      <BranchSelectModal
        isOpen={branchPickerOpen}
        onClose={() => setBranchPickerOpen(false)}
        branches={branches}
        includeAll={false}
        onSelect={(branch) => {
          if (branch) openBranchSettings(branch);
        }}
      />
    </>
  );
}

export default DashboardMenu;
